import { memo, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelectedCampaign } from '../../../../../store/campaignsStore';
import {
  REBATE_CARD_INFO_PLACEHOLDER,
  findCurrentLevel,
  getNextWinDisplay,
  isLevelUnlocked,
  isLevelFinished,
  hasSpentBetReachedMaxAmount,
  isRebateSource,
} from '../../../../../utils/variants/rebateHelpers';
import { useCurrency } from '../../../../../../../store/app/appStore';
import CampaignTable from '../../../../shared/CampaignTable';
import type { IRebateCampaign } from '../../../../../types/campaign.types';
import Image from '../../../../../../../shared/components/Image';

import styles from './WinningsTab.module.css';
import tableStyles from '../../../../shared/CampaignTable/CampaignTable.module.css';
import RebateProgressItem from '../../RebateProgressItem/RebateProgressItem';
import { useCampaignStatus } from '../../../../../../../features/campaigns/hooks';

const TABLE_COLUMNS = [
  {
    name: 'Levels',
    key: 'rebate.levels',
    cellClassName: '',
  },
  {
    name: 'Rebate',
    key: 'rebate.percent',
    className: '',
  },
  {
    name: 'Win',
    key: 'rebate.win',
    className: '',
  },
];

interface OverviewContainerProps {
  campaign: IRebateCampaign;
  campaignCurrency: string;
}

const OverviewContainer: React.FC<OverviewContainerProps> = ({ campaign, campaignCurrency }) => {
  const { t } = useTranslation();
  const userProgress = campaign.userProgress;
  const { isFinished, isPromo } = useCampaignStatus(campaign.id);
  const claimedWin = (userProgress?.claimedWin ?? 0).toFixed(2);
  const nextWin = getNextWinDisplay(campaign, { isPromo, isFinished });

  return (
    <div className={styles.overviewContainer}>
      <div className={`${styles.overviewContainerCell} ${styles.left}`}>
        <span className={styles.overviewCellHeading}>{t('campaigns.claimed_win', 'Claimed Win')}</span>
        <span className={styles.overviewCellData}>{claimedWin}<span className={styles.overviewCellCurrency}>{campaignCurrency}</span></span>
      </div>
      <div className={`${styles.overviewContainerCell} ${styles.right}`}>
        <span className={styles.overviewCellHeading}>{t('rebate.next_win', 'Next Win')}</span>
        <span className={styles.overviewCellData}>{nextWin}{nextWin !== REBATE_CARD_INFO_PLACEHOLDER && <span className={styles.overviewCellCurrency}>{campaignCurrency}</span>}</span>
      </div>
    </div>
  );
};

interface LevelIconProps {
  unlocked: boolean
  inProgress: boolean
}

const LevelIcon: React.FC<LevelIconProps> = ({ unlocked, inProgress }) => {
  let iconName = 'Lock';
  if (unlocked) iconName = inProgress ? 'InProgress' : 'Unlock';

  return (
    <Image
      src={`/campaigns/icons/level${iconName}`}
      manualAffix=".svg"
      className={`${styles.levelIcon} ${unlocked ? (inProgress ?  styles.levelIconInProgress : styles.levelIconUnlocked) : ''}`}
    />
  );
};

const WinningsTab: React.FC = () => {
  const selectedCampaign = useSelectedCampaign();
  const { isFinished } = useCampaignStatus(selectedCampaign?.id);
  const currency = useCurrency();
  const { t } = useTranslation();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [progressItemWidth, setProgressItemWidth] = useState<number | undefined>(undefined);

  const cashbackLevels = selectedCampaign && isRebateSource(selectedCampaign) ? selectedCampaign.cashbackLevels : undefined;

  // Sizes the current level's progress bar to the widest win label in the table: measures every
  // `.Win` label, exposes the max as `--progress-item-width` on the wrapper, and re-measures on label resize.
  useLayoutEffect(() => {
    const container = wrapperRef.current;
    if (!container || !cashbackLevels || cashbackLevels.length === 0) return undefined;

    const measure = () => {
      const winEls = container.querySelectorAll<HTMLElement>(`.${styles.win}`);
      let maxWidth = 0;
      winEls.forEach((el) => {
        maxWidth = Math.max(maxWidth, el.getBoundingClientRect().width);
      });
      if (maxWidth > 0) setProgressItemWidth(maxWidth);
    };

    measure();

    const resizeObserver = new ResizeObserver(measure);
    container.querySelectorAll<HTMLElement>(`.${styles.win}`).forEach((el) => resizeObserver.observe(el));

    return () => resizeObserver.disconnect();
  }, [cashbackLevels]);

  if (!selectedCampaign || !isRebateSource(selectedCampaign)) return null;

  const campaignCurrency = selectedCampaign.baseCurrency || currency;
  const userProgress = selectedCampaign.userProgress;
  const spentBetAmount = userProgress?.spentBetAmount;
  const currentLevel = userProgress && !hasSpentBetReachedMaxAmount(selectedCampaign)
    ? findCurrentLevel(selectedCampaign.cashbackLevels, userProgress.spentBetAmount)
    : undefined;
  // Spend reached the level ceiling is count as a finished level.
  const inProgressLevelNumber = currentLevel && userProgress && !isLevelFinished(userProgress.spentBetAmount, currentLevel)
    ? currentLevel.levelNumber
    : undefined;

  return (
    <div
      className={styles.winningsTabWrapper}
      ref={wrapperRef}
      style={progressItemWidth ? ({ '--progress-item-width': `${progressItemWidth}px` } as React.CSSProperties) : undefined}
    >
      <OverviewContainer campaign={selectedCampaign} campaignCurrency={campaignCurrency} />
      <CampaignTable columns={TABLE_COLUMNS} tableType='winnings' className='rebate-winnings'>
        {selectedCampaign.cashbackLevels.map((level) => {
          const levelNumber = level.levelNumber;
          const betRangeFrom = level.betRangeFrom;
          const betRangeTo = level.betRangeTo;
          const rebatePercent = level.rebatePercent.toFixed(1);
          const isCurrentLevelUnlocked = isLevelUnlocked(spentBetAmount, level);
          const win = level.maxPayoutAmount.toFixed(2);
          const isLevelInProgress = levelNumber === inProgressLevelNumber;
          const isLevelDisabled = isFinished && !isLevelFinished(spentBetAmount, level);

          return (
            <tr key={`level-${levelNumber}`} className={`${tableStyles.tableRow} ${styles.tableRow}`} data-disabled={isLevelDisabled || undefined}>
              <td className={`${tableStyles.tableCell} ${styles.levelsCellContainer}`}>
                <span className={styles.levelsCellTop}>
                  {t('rebate.level', { number: levelNumber, defaultValue: 'Level {{number}}' })}
                </span>
                <span className={styles.levelsCellBottom}>
                  {t('campaigns.bet_amount', {
                    amount: `${betRangeFrom} - ${betRangeTo}`,
                    currency: campaignCurrency,
                    defaultValue: '{{amount}} {{currency}} bet',
                  })}
                </span>
              </td>
              <td className={`${tableStyles.tableCell} ${styles.percentageCell}`}>
                <LevelIcon unlocked={isCurrentLevelUnlocked} inProgress={isLevelInProgress} />
                <span className={styles.percentage}>{rebatePercent}%</span>
              </td>
              <td className={`${tableStyles.tableCell} ${styles.winCell}`}>
                <div className={styles.win}>+ {win}<span>{campaignCurrency}</span></div>
                {
                  isLevelInProgress && <RebateProgressItem
                    cashbackLevel={level}
                    userProgress={userProgress}
                    variant='winnings-tab'
                  />
                }
              </td>
            </tr>
          );
        })}
      </CampaignTable>
    </div>
  );
};

export default memo(WinningsTab);
